import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { whatsappUrl } from '../lib/whatsapp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const DEFAULT_MESSAGES = {
  live:
    'Namaste Sanjay Ji, I am interested in joining the live Siddha Kriya classes. Could you please share details about the timings and batches?',
  healing:
    'Namaste Sanjay Ji, I would like to book a Siddha healing session. Could you please share your availability and details?',
  general: 'Namaste Sanjay Ji, I have a question.',
};

const TITLES = {
  live: 'Enquire about Live Classes',
  healing: 'Book a Siddha Healing Session',
  general: 'Send a Message',
};

// Only digits — stripped of spaces, dashes, parentheses, leading + etc.
const digitsOnly = (s = '') => (s || '').replace(/\D+/g, '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Replaces the direct wa.me link on Live Classes / Siddha Healing CTAs.
 * Collects name, email, phone, message (prefilled from the signed-in user and
 * the use-case) and POSTs /api/enquiry/send which emails the admin — the
 * personal number is never exposed on the page.
 */
const EnquiryModal = ({ open, onClose, kind = 'general' }) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(user?.name || '');
    setEmail(user?.email || '');
    setPhone(user?.phone || '');
    setMessage(DEFAULT_MESSAGES[kind] || DEFAULT_MESSAGES.general);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, kind, user, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMsg = message.trim();
    const phoneDigits = digitsOnly(phone);

    if (!trimmedName) { toast.error('Please enter your name.'); return; }
    if (phoneDigits.length < 10) { toast.error('Please enter a valid phone number (at least 10 digits).'); return; }
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) { toast.error('Please enter a valid email address.'); return; }
    if (!trimmedMsg) { toast.error('Please add a short message.'); return; }

    setBusy(true);

    // Build the WhatsApp message that Sanjay Ji will see — includes the full
    // user context so the chat has everything needed to respond.
    const waText =
      `${trimmedMsg}\n\n` +
      `— Name: ${trimmedName}\n` +
      `— Phone: ${phoneDigits}\n` +
      `— Email: ${trimmedEmail}`;
    const waLink = whatsappUrl(waText);

    // Fire-and-forget the backend email as a backup so Sanjay Ji still
    // receives the enquiry even if the user never completes the WA redirect.
    axios.post(`${API}/enquiry/send`, {
      kind, name: trimmedName, email: trimmedEmail, phone: phoneDigits, message: trimmedMsg,
    }).catch(() => { /* non-blocking */ });

    toast.success('Opening WhatsApp to send your enquiry…');
    // Open WA in a new tab so the modal state isn't lost on mobile back-nav.
    window.open(waLink, '_blank', 'noopener,noreferrer');
    setBusy(false);
    onClose();
  };

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="enquiry-form-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        data-testid="form-enquiry-overlay"
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(145deg, #1a0f05 0%, #0f0802 100%)',
          border: '1px solid rgba(212,168,67,0.35)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-primary/70 hover:text-primary"
          data-testid="enquiry-modal-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="p-6 sm:p-7">
          <h3 className="text-xl font-heading golden-text text-glow tracking-wide text-center mb-5">
            {TITLES[kind] || TITLES.general}
          </h3>

          <label className="block mb-3">
            <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60">Name *</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              className="mt-1 w-full rounded-lg bg-primary/[0.04] border border-primary/25 px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
              data-testid="enquiry-name"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60">Email *</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full rounded-lg bg-primary/[0.04] border border-primary/25 px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
                data-testid="enquiry-email"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60">Phone (WhatsApp) *</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                inputMode="tel"
                pattern="[0-9+\s\-()]{10,}"
                placeholder="e.g. 9876543210"
                className="mt-1 w-full rounded-lg bg-primary/[0.04] border border-primary/25 px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
                data-testid="enquiry-phone"
              />
            </label>
          </div>

          <label className="block mb-5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60">Message *</span>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              minLength={5}
              className="mt-1 w-full rounded-lg bg-primary/[0.04] border border-primary/25 px-3 py-2 text-sm text-primary focus:outline-none focus:border-primary/60"
              data-testid="enquiry-message"
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="enquiry-send"
            className="ornate-button w-full py-3 text-sm font-heading uppercase tracking-widest text-primary rounded-lg disabled:opacity-60"
          >
            {busy ? 'Opening WhatsApp…' : 'Continue on WhatsApp'}
          </button>
          <p className="mt-3 text-[11px] text-primary/55 text-center italic leading-relaxed">
            Your details will be prefilled in a WhatsApp message to Sanjay Ji.
            A copy is also emailed to him as a backup.
          </p>
        </div>
      </form>
    </div>
  );
  return createPortal(node, document.body);
};

export default EnquiryModal;
